import type { AnalyticsClient } from '../../analytics/client';
import type { SessionTracker } from '../../analytics/session-tracker';
import type { BotStrategySnapshot } from '../../bot/bot';
import type { ContentSnapshot, PlayerId } from '../../engine/types';
import type { DiagnosticTerminationReason, MatchDiagnosticBundle } from '../../diagnostics/analyzer';
import type { I18nPort } from '../../i18n/i18n-service';
import type { RoomSession } from '../../network/contracts';
import { clearStartupRecovery, scheduleStartupReload } from '../startup-recovery';
import { createBotMatchContext, type MatchContext } from '../match-context';
import type { AppSettings } from '../settings';
import { LocalBotDriver } from '../drivers/local-bot-driver';
import { LockstepNetworkDriver } from '../drivers/lockstep-network-driver';
import { ReplayDriver } from '../drivers/replay-driver';
import type { MatchFinalizer } from '../match-finalizer';
import { requireElement } from '../dom';
import type { MatchController, CompletedMatchRuntime } from './match-controller';
import type { LobbyController } from './lobby-controller';
import type { NetworkReadyConnection } from './network-session-controller';
import type { NetworkPresentationController } from './network-presentation-controller';
import { configuredNetworkBaseUrl, NetworkApiClient } from '../../network/api-client';
import type { ResultController } from './result-controller';
import type { ToastController } from './toast-controller';
import { battleRules } from '../battle-rules';

export type SessionState =
  | Readonly<{ phase: 'menu' }>
  | Readonly<{ phase: 'starting'; mode: 'bot' | 'network' | 'replay' }>
  | Readonly<{ phase: 'match'; context: MatchContext; replay: boolean }>
  | Readonly<{ phase: 'result'; bundle: MatchDiagnosticBundle }>;

interface ActiveFinalization {
  readonly replay: boolean;
  readonly botStrategy: BotStrategySnapshot | null;
  readonly networkSession: RoomSession | null;
}

export interface SessionControllerOptions {
  readonly content: ContentSnapshot;
  readonly settings: AppSettings;
  readonly i18n: I18nPort;
  readonly lobby: LobbyController;
  readonly match: MatchController;
  readonly result: ResultController;
  readonly finalizer: MatchFinalizer;
  readonly toast: ToastController;
  readonly analytics: AnalyticsClient;
  readonly sessionTracker: SessionTracker;
  readonly fastMode: boolean;
  readonly networkPresentation: NetworkPresentationController;
}

export class SessionController {
  private readonly networkApi = new NetworkApiClient(configuredNetworkBaseUrl());
  private readonly startScreen = requireElement<HTMLElement>('#startScreen');
  private readonly resultScreen = requireElement<HTMLElement>('#resultScreen');
  private readonly playButton = requireElement<HTMLButtonElement>('#playButton');
  private stateValue: SessionState = { phase: 'menu' };
  private active: ActiveFinalization | null = null;
  private finishing = false;
  private reportedAbandoned = false;

  constructor(private readonly options: SessionControllerOptions) {
    this.playButton.addEventListener('click', () => { void this.startBot(); });
  }

  get state(): SessionState { return this.stateValue; }

  async startBot(replayBundle?: MatchDiagnosticBundle): Promise<void> {
    if (this.stateValue.phase === 'starting') return;
    this.options.lobby.commitPlayerName();
    this.options.match.unlockAudio();
    this.stateValue = { phase: 'starting', mode: replayBundle ? 'replay' : 'bot' };
    this.playButton.disabled = true;
    this.playButton.textContent = this.options.i18n.t('start.loadingArena');
    try {
      if (replayBundle) await this.startReplay(replayBundle);
      else await this.startLocalBot();
      clearStartupRecovery(sessionStorage);
    } catch (error) {
      await this.persistStartupFailure(error);
      this.options.match.abandon(false);
      this.showMenu();
      if (scheduleStartupReload(sessionStorage, error, replayBundle ? 'replay' : 'live')) { location.reload(); return; }
      clearStartupRecovery(sessionStorage);
      this.options.toast.show(this.options.i18n.t('toast.startFailed', { error: String(error) }), 6);
    } finally {
      this.playButton.disabled = false;
      this.playButton.textContent = this.options.i18n.t('start.playBot');
    }
  }

  async startNetwork(connection: NetworkReadyConnection): Promise<void> {
    if (this.stateValue.phase !== 'menu') { connection.peer.close(); return; }
    this.options.match.unlockAudio();
    this.options.networkPresentation.attach(connection.peer);
    this.stateValue = { phase: 'starting', mode: 'network' };
    const localSide = connection.session.seat === 'seat-a' ? 'player' : 'bot';
    const context: MatchContext = Object.freeze({
      matchId: `network-${connection.config.networkMatchId}-${connection.session.seat}`,
      mode: 'network',
      resultTrust: 'local',
      networkMatchId: connection.config.networkMatchId,
      localSide,
      participants: Object.freeze(Object.fromEntries(Object.values(connection.config.participants).map((participant) => {
        const side = participant.seat === 'seat-a' ? 'player' : 'bot';
        return [side, Object.freeze({ side, kind: 'human' as const, displayName: participant.displayName, networkPlayerId: participant.networkPlayerId })];
      })) as unknown as Readonly<Record<PlayerId, MatchContext['participants'][PlayerId]>>),
    });
    this.active = { replay: false, botStrategy: null, networkSession: connection.session };
    try {
      await this.options.match.start({
        context,
        seed: connection.config.seed,
        arenaId: connection.config.arenaId,
        decks: connection.config.decks,
        rules: connection.config.rules,
        diagnosticProfile: 'full',
        afterEngineCreated: (engine) => {
          engine.journal.record({ tick: 0, phase: 'performance', source: 'network-link', code: 'NETWORK_LINK_READY', payload: connection.peer.linkStats });
        },
        createDriver: (engine) => new LockstepNetworkDriver(connection.config.networkMatchId, connection.session.seat, connection.peer, {
          onDesync: (details) => {
            engine.journal.record({ tick: details.tick, phase: 'simulation', source: 'lockstep', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: details });
            void this.abortNetworkMatch(connection.session, 'network.claimDisputed', 'desync');
          },
          onDisconnect: () => this.options.networkPresentation.connectionStatus(this.options.i18n.t('network.reconnecting')),
          onReconnect: () => {
            this.options.networkPresentation.connectionStatus(null);
            this.options.toast.show(this.options.i18n.t('network.reconnected'));
          },
          onConnectionLost: () => {
            engine.journal.record({ tick: engine.currentTick, phase: 'presentation', source: 'network-link', level: 'error', code: 'NETWORK_CONNECTION_LOST', payload: connection.peer.linkStats });
            void this.abortNetworkMatch(connection.session, 'network.connectionLost', 'connection-lost');
          },
        }),
      }, this.options.settings.quality);
      this.enterMatch(context, false);
    } catch (error) {
      await this.persistStartupFailure(error);
      await this.networkApi.abandonRoom(connection.session).catch(() => undefined);
      connection.peer.close();
      this.options.networkPresentation.detach();
      this.active = null;
      this.showMenu();
      this.options.toast.show(this.options.i18n.t('network.unavailable', { error: String(error) }), 6);
    }
  }

  async finish(runtime: CompletedMatchRuntime): Promise<void> {
    if (this.finishing || !this.active) return;
    this.finishing = true;
    try {
      const bundle = await this.options.finalizer.finish(runtime, this.active);
      this.options.match.abandon(false);
      this.stateValue = { phase: 'result', bundle };
    } finally {
      this.finishing = false;
    }
  }

  replay(bundle: MatchDiagnosticBundle): void { void this.startBot(bundle); }
  again(): void { void this.startBot(); }

  showMenu(): void {
    this.options.match.abandon(false);
    this.options.result.hide();
    this.startScreen.classList.add('open');
    this.resultScreen.classList.remove('open');
    this.stateValue = { phase: 'menu' };
    this.active = null;
    this.options.networkPresentation.detach();
    this.options.sessionTracker.setMode('menu');
  }

  visibilityChanged(hidden: boolean): void {
    if (hidden && this.options.match.active && !this.options.match.paused && this.stateValue.phase === 'match' && this.stateValue.context.mode === 'bot') this.options.match.togglePause();
    this.options.sessionTracker.setVisible(!hidden);
  }

  reportPageHide(): void {
    void this.finalizeAbandoned('pagehide', false);
    this.options.analytics.session(this.options.sessionTracker.snapshot(), true);
  }

  reportFatalError(reason: 'uncaught-error' | 'unhandled-rejection', error: unknown): void {
    const runtime = this.options.match.captureRuntime();
    if (runtime) {
      const sanitized = String(error instanceof Error ? error.stack ?? error.message : error).replace(/https?:\/\/\S+/g, '[url]').slice(0, 4_000);
      runtime.engine.journal.record({ tick: runtime.engine.currentTick, phase: 'presentation', source: 'window-error', level: 'error', code: reason === 'uncaught-error' ? 'UNCAUGHT_ERROR' : 'UNHANDLED_REJECTION', payload: { error: sanitized } });
    }
    void this.finalizeAbandoned(reason, true);
  }

  private async startLocalBot(): Promise<void> {
    this.options.networkPresentation.detach();
    const seed = Date.now() >>> 0;
    const prepared = this.options.lobby.prepareBot(seed);
    const context = createBotMatchContext(`battle-${seed}-${Date.now().toString(36)}`, this.options.lobby.displayName);
    this.active = { replay: false, botStrategy: prepared.strategy, networkSession: null };
    await this.options.match.start({
      context,
      seed,
      arenaId: 'basecamp',
      decks: { player: this.options.lobby.decks.player, bot: prepared.strategy.selectedDeck },
      rules: this.rules(),
      diagnosticProfile: 'full',
      botStrategy: prepared.strategy,
      createDriver: (engine) => new LocalBotDriver(this.options.lobby.createBotController(seed, engine, prepared)),
      afterEngineCreated: (engine) => {
        engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-deck-selector', code: 'BOT_DECK_EVALUATED', payload: { botId: prepared.definition.id, deckPolicyId: prepared.definition.deckPolicyId, evaluatedCount: prepared.selection.evaluatedCount, topCandidates: prepared.selection.topCandidates } });
        engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-deck-selector', code: 'BOT_DECK_SELECTED', cardIds: prepared.strategy.selectedDeck, payload: { botId: prepared.definition.id, botVersion: prepared.definition.version, deckPolicyId: prepared.definition.deckPolicyId, deckPolicyVersion: prepared.definition.deckPolicyVersion, deck: prepared.strategy.selectedDeck, score: prepared.strategy.deckScore, rationale: prepared.strategy.rationale } });
        engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-controller', code: 'BOT_STRATEGY_SNAPSHOT_CREATED', payload: prepared.strategy });
      },
    }, this.options.settings.quality);
    this.enterMatch(context, false);
  }

  private async startReplay(bundle: MatchDiagnosticBundle): Promise<void> {
    const replay = bundle.replay;
    const localSide = replay.match.localSide;
    const opponentSide: PlayerId = localSide === 'player' ? 'bot' : 'player';
    const context: MatchContext = Object.freeze({
      matchId: `replay-${replay.seed}-${Date.now().toString(36)}`,
      ...replay.match,
      participants: Object.freeze({
        [localSide]: Object.freeze({ side: localSide, kind: 'human' as const, displayName: this.options.lobby.displayName, networkPlayerId: null }),
        [opponentSide]: Object.freeze({ side: opponentSide, kind: replay.match.mode === 'bot' ? 'bot' as const : 'human' as const, displayName: null, networkPlayerId: null }),
      }) as Readonly<Record<PlayerId, MatchContext['participants'][PlayerId]>>,
    });
    this.active = { replay: true, botStrategy: replay.botStrategy ?? null, networkSession: null };
    await this.options.match.start({
      context,
      seed: replay.seed,
      arenaId: replay.arenaId,
      decks: replay.decks,
      rules: replay.rules,
      diagnosticProfile: 'full',
      ...(replay.botStrategy ? { botStrategy: replay.botStrategy } : {}),
      createDriver: () => new ReplayDriver(replay.commands, localSide),
    }, this.options.settings.quality);
    this.enterMatch(context, true);
    this.options.analytics.event('replay_started', { sourceMatchId: bundle.manifest.matchId });
  }

  private enterMatch(context: MatchContext, replay: boolean): void {
    this.startScreen.classList.remove('open');
    this.resultScreen.classList.remove('open');
    this.options.result.hide();
    this.stateValue = { phase: 'match', context, replay };
    this.reportedAbandoned = false;
    if (replay) this.options.sessionTracker.setMode('gameplay');
    else {
      this.options.sessionTracker.matchStarted();
      this.options.analytics.event('match_started', { matchId: context.matchId, matchMode: context.mode, networkMatchId: context.networkMatchId });
    }
  }

  private async abortNetworkMatch(session: RoomSession, messageKey: 'network.connectionLost' | 'network.claimDisputed', reason: 'connection-lost' | 'desync'): Promise<void> {
    if (this.active?.networkSession?.roomId !== session.roomId || (this.stateValue.phase !== 'match' && this.stateValue.phase !== 'starting')) return;
    await this.finalizeAbandoned(reason, true, messageKey);
  }

  private async finalizeAbandoned(reason: Exclude<DiagnosticTerminationReason, 'running' | 'completed'>, returnToMenu: boolean, messageKey?: 'network.connectionLost' | 'network.claimDisputed'): Promise<void> {
    if (this.finishing || !this.active || this.stateValue.phase !== 'match' || this.stateValue.replay) return;
    this.finishing = true;
    const active = this.active;
    this.options.match.markFinishing();
    const runtime = this.options.match.captureRuntime();
    if (this.stateValue.phase === 'match' && !this.reportedAbandoned) {
      this.reportedAbandoned = true;
      this.options.sessionTracker.matchAbandoned();
      this.options.analytics.event('match_abandoned', { matchId: this.stateValue.context.matchId, matchMode: this.stateValue.context.mode, networkMatchId: this.stateValue.context.networkMatchId, reason });
    }
    try {
      const roomAbandon = active.networkSession
        ? this.networkApi.abandonRoom(active.networkSession).catch(() => undefined)
        : Promise.resolve();
      if (runtime) await this.options.finalizer.abort(runtime, active, reason);
      await roomAbandon;
      if (returnToMenu) {
        this.showMenu();
        if (messageKey) this.options.toast.show(this.options.i18n.t(messageKey), 8);
      }
    } finally {
      this.finishing = false;
    }
  }

  private async persistStartupFailure(error: unknown): Promise<void> {
    const runtime = this.options.match.captureRuntime();
    const active = this.active;
    if (!runtime || !active || active.replay) return;
    const sanitized = String(error instanceof Error ? error.stack ?? error.message : error).replace(/https?:\/\/\S+/g, '[url]').slice(0, 4_000);
    runtime.engine.journal.record({ tick: runtime.engine.currentTick, phase: 'presentation', source: 'session-startup', level: 'error', code: 'MATCH_STARTUP_FAILED', payload: { error: sanitized } });
    await this.options.finalizer.abort(runtime, active, 'startup-failure').catch(() => undefined);
  }

  private rules() {
    return battleRules(this.options.fastMode);
  }
}
